import type { ApiAssistantRateBand, ApiAssistantRentalCar } from 'src/types/assistant';

import { formatAmount } from 'src/utils/money';
import { fromDailyPrice } from 'src/utils/rentalPricing';

/**
 * The price row of a car for rent in the panel (app#362, Tim 2026-10-30: "the price for the
 * dates"). The assistant knows the person's dates, so the card says what exactly that booking
 * costs a day and for how many days — the platform's quote, the one the Trip cost line reads — and,
 * when the booking is long enough for a cheaper band, what that band saves against 1–3 days. With
 * no dates it is the catalogue card's price, by the catalogue's own rule (`fromDailyPrice`). Every
 * number is the api's; nothing is counted here.
 */
export interface AssistantRentalPrice {
  /** The number with its currency, as the card sets it: "85 EUR", "from 65 EUR". */
  amount: string;
  /** The booking's length beside the number: "7 days". None without dates. */
  days?: string;
  /**
   * The line under the price, in its parts — "4–7 day rate: **10 EUR a day less** than for 1–3
   * days, **70 EUR off this trip**" — so the card can set the two savings apart from the words
   * around them (Tim, 2026-10-31: the line grey, the figures white).
   */
  saving?: { band: string; perDay: string; against: string; total: string };
}

const dayWords = (days: number): string => `${days} ${days === 1 ? 'day' : 'days'}`;

/** "4–7 day", "16+ day": a band as an adjective. */
const bandAdjective = (band: ApiAssistantRateBand): string =>
  band.maxDays === null ? `${band.minDays}+ day` : `${band.minDays}–${band.maxDays} day`;

/** "1–3 days", "16+ days". */
const bandDays = (band: ApiAssistantRateBand): string => `${bandAdjective(band)}s`;

export const assistantRentalPrice = (car: ApiAssistantRentalCar): AssistantRentalPrice | null => {
  const { booking, currency } = car;

  if (!currency) return null;

  if (booking) {
    const { saving } = booking;

    return {
      amount: `${formatAmount(booking.rate.dailyPrice)} ${currency}`,
      days: dayWords(booking.days),
      saving: saving
        ? {
            band: `${bandAdjective(booking.rate)} rate`,
            perDay: `${formatAmount(saving.perDay)} ${currency} a day less`,
            against: `than for ${bandDays(saving.against)}`,
            total: `${formatAmount(saving.total)} ${currency} off this trip`,
          }
        : undefined,
    };
  }

  if (car.dailyPrice == null) return null;

  if (car.tierPrices) {
    const { price, isFrom } = fromDailyPrice({ ...car.tierPrices, dailyPrice: car.dailyPrice });

    return { amount: `${isFrom ? 'from ' : ''}${formatAmount(price)} ${currency}` };
  }

  // An api before app#362 sends the 1–3 day price alone: the line the panel always wrote.
  return { amount: `from ${formatAmount(car.dailyPrice)} ${currency}` };
};
